import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import GlyphCalendar from '@/assets/icons/glyph_calendar.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { Caption } from '@/components/atoms/Eyebrow';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip from '@/components/atoms/Tooltip';
import { MetricSummaryRow } from '@/components/molecules/MetricSummaryRow';
import { useLiveBioscopeData } from '@/contexts/mqtt/useLiveBioscopeData';
import type { DomainCageStatus, HistoricalBioscopeInfo } from '@/domain/cages';
import { diffDates, formatDateDisplay, formatDateTimeWorded, now, toDate } from '@/utils/date';
import {
    formatNumberByLocale,
    formatUniversalUnitByLocale,
    parseLocale,
} from '@/utils/localeFormatters';

import styles from './BioscopeStatusBox.module.scss';

interface BioscopeStatusBoxProps {
    cageStatus?: DomainCageStatus | null;
    measurementCount: number;
    isLoading?: boolean;
    isOptoScaleAdmin?: boolean;
    showBioscopeInspectionLink?: boolean;
    bioscopeInspectionLink?: string;
    historicalBioscopeInspectionLink?: (bioscopeId: number) => string;
    isSmolt?: boolean;
}

interface HistoricalBioscopeGroup {
    bioscopeId: number;
    periods: Pick<HistoricalBioscopeInfo, 'from' | 'to'>[];
}

/** One entry per bioscope, the one still deployed first, then the most recently removed. */
function groupHistoricalBioscopes(historical: HistoricalBioscopeInfo[]): HistoricalBioscopeGroup[] {
    const groups = historical.reduce<Record<number, HistoricalBioscopeGroup['periods']>>(
        (acc, curr) => {
            if (!acc[curr.bioscopeId]) {
                acc[curr.bioscopeId] = [];
            }
            acc[curr.bioscopeId].push({ from: curr.from, to: curr.to });
            return acc;
        },
        {}
    );

    return Object.entries(groups)
        .map(([bioscopeId, periods]) => ({ bioscopeId: Number(bioscopeId), periods }))
        .sort((a, b) => {
            const aTo = a.periods[0].to;
            const bTo = b.periods[0].to;
            if (aTo === null) return -1;
            if (bTo === null) return 1;
            return new Date(bTo).getTime() - new Date(aTo).getTime();
        });
}

export default function BioscopeStatusBox(props: BioscopeStatusBoxProps) {
    const {
        cageStatus,
        measurementCount,
        isLoading = false,
        isOptoScaleAdmin = false,
        showBioscopeInspectionLink,
        bioscopeInspectionLink,
        historicalBioscopeInspectionLink,
        isSmolt = false,
    } = props;

    const { t, i18n } = useTranslation();

    const locale = parseLocale(i18n.language);
    const currentBioscopeId = cageStatus?.currentBioscope?.id ?? null;
    const live = useLiveBioscopeData(currentBioscopeId);

    const activeBioscopeId = currentBioscopeId != null ? String(currentBioscopeId) : '-';

    const depth = formatUniversalUnitByLocale(live.depth, 'm', locale, 1, '-');
    const pitchText = formatUniversalUnitByLocale(live.pitch, '°', locale, 1, '-');
    const rollText = formatUniversalUnitByLocale(live.roll, '°', locale, 1, '-');
    const measurementCountText = formatNumberByLocale(measurementCount, locale, 0);

    const lastMoveDate = toDate(cageStatus?.lastData?.lastMoveFromDate);
    const numberOfDaysSinceMove = lastMoveDate ? diffDates(now(), lastMoveDate, 'day') : 0;

    const historicalBioscopes = cageStatus?.historicalBioscopes;
    const groupedHistoricalBioscopes = useMemo(
        () => groupHistoricalBioscopes(historicalBioscopes ?? []),
        [historicalBioscopes]
    );

    return (
        <div className={styles.boxContainer}>
            <MetricSummaryRow
                label={t('common.labels.lastMeasurement')}
                value={formatDateTimeWorded(cageStatus?.lastData?.timestampLast)}
                isLoading={isLoading}
                tooltip={t('descriptions.bioscopeStatus.lastMeasurement')}
            />
            <hr className={styles.divider} />
            <div className={styles.twoColumns}>
                <MetricSummaryRow
                    label={t('molecules.bioscopeStatusBox.liveDepth')}
                    value={depth}
                    isLoading={isLoading}
                    tooltip={t('descriptions.bioscopeStatus.depth')}
                />
                <MetricSummaryRow
                    label={t('molecules.bioscopeStatusBox.pitchRoll')}
                    value={`${pitchText} , ${rollText}`}
                    isLoading={isLoading}
                    tooltip={t('descriptions.bioscopeStatus.pitchAndRoll')}
                />
                {isSmolt && (
                    <MetricSummaryRow
                        label={t('molecules.bioscopeStatusBox.numberOfDays')}
                        value={`${formatNumberByLocale(numberOfDaysSinceMove, locale, 0)} ${t('molecules.bioscopeStatusBox.days')}`}
                        isLoading={isLoading}
                        tooltip={`${t(
                            'descriptions.bioscopeStatus.numberOfDaysSinceMove'
                        )} {${formatDateDisplay(lastMoveDate)}}.`}
                    />
                )}
                <MetricSummaryRow
                    label={t('molecules.bioscopeStatusBox.measuredFishTotal')}
                    value={measurementCountText}
                    isLoading={isLoading}
                    tooltip={t('descriptions.bioscopeStatus.measurementCount')}
                />
                <MetricSummaryRow
                    label={t('molecules.bioscopeStatusBox.activeBioscope')}
                    value={activeBioscopeId}
                    isLoading={isLoading}
                />
            </div>

            {isOptoScaleAdmin && (
                <>
                    <hr className={styles.divider} />
                    <div className={styles.optoscaleAdminSection}>
                        <img src="/favicon.ico" alt="" />
                        <p className={styles.adminNotice}>
                            {t('molecules.bioscopeStatusBox.onlyVisibleForOptoscaleAdmin')}
                        </p>
                    </div>
                    <Caption className={styles.label}>
                        {t('molecules.bioscopeStatusBox.historicalBioscopes')}
                    </Caption>
                    <div className={styles.historicalBioscope}>
                        {groupedHistoricalBioscopes.map(({ bioscopeId, periods }) => (
                            <Tooltip
                                key={bioscopeId}
                                layout="fit"
                                content={
                                    <div className={styles.toolTipContent}>
                                        {periods.map((p) => (
                                            <div key={`${p.from}-${p.to ?? 'present'}`}>
                                                {formatDateTimeWorded(p.from)}
                                                <span className={styles.fromToSeparator}>–</span>
                                                {p.to
                                                    ? formatDateTimeWorded(p.to)
                                                    : t('molecules.bioscopeStatusBox.present')}
                                            </div>
                                        ))}
                                    </div>
                                }>
                                <div className={styles.linkAndIcon}>
                                    {/* A router link: a plain href reloaded the whole app. */}
                                    {historicalBioscopeInspectionLink ? (
                                        <Link
                                            to={historicalBioscopeInspectionLink(bioscopeId)}
                                            className={styles.link}>
                                            {bioscopeId}
                                        </Link>
                                    ) : (
                                        <span className={styles.link}>{bioscopeId}</span>
                                    )}
                                    <SvgIcon
                                        icon={GlyphCalendar}
                                        size="large"
                                        color={
                                            periods.some((p) => !p.to)
                                                ? 'var(--color-success-fill)'
                                                : 'var(--color-text-secondary)'
                                        }
                                    />
                                </div>
                            </Tooltip>
                        ))}
                    </div>

                    <div className={styles.twoColumns}>
                        <MetricSummaryRow
                            label={t('molecules.bioscopeStatusBox.lastActivity')}
                            value={formatDateTimeWorded(cageStatus?.lastData?.lastAlive)}
                            isLoading={isLoading}
                            tooltip={t('descriptions.bioscopeStatus.lastLifesign')}
                        />
                        <MetricSummaryRow
                            label={t('molecules.bioscopeStatusBox.lastMqttMessage')}
                            value={formatDateTimeWorded(live.lastMessageAt)}
                            isLoading={isLoading}
                            tooltip={t('descriptions.bioscopeStatus.lastMqttMessage')}
                        />
                    </div>
                </>
            )}

            {(isOptoScaleAdmin || showBioscopeInspectionLink) && bioscopeInspectionLink && (
                <Button
                    variant={'secondary'}
                    trackingId="bioscope_status_inspect"
                    to={bioscopeInspectionLink}>
                    {t('molecules.bioscopeStatusBox.bioscopeInspection')}
                </Button>
            )}
        </div>
    );
}
